// Anatomy › Discover — the scout run behind the anatomy page's agentic tab.
//
// The Structure tab computes WHERE the scope's mass is with two fixed
// heuristics (highest-volume descent + distinctiveness columns); this module
// answers WHAT the work is. A scoped sub-agent (the same analytics tools the
// wiki scout and the constitution profiler use) samples the scope and returns
// a typed report — profile, themes, spread, pockets — every claim carrying
// the `basis` it rests on.
//
// A claim may carry a click-through `target` (a scope step), but only with an
// id copied from rows the scout actually queried; normalizeAnatomyReport
// drops targets whose dim isn't a real dashboard dimension. The run also
// returns a methods TRAIL derived from the sub-agent's own message history —
// the queries beside the claims are what keep the report honest.
//
// SCOPE is enforced mechanically, not by the prompt (scoutScope.js): the
// scout's analytics tool flags queries that never reference the scope's ids,
// and every target is probed for activity INSIDE the scope after the run.
// A target with none is stripped (the claim degrades to plain text) so a
// click-through never points outside the scope it was reported for.

import { runWithConcurrency, startSubAgent } from "./subAgent.js";
import { buildDiscoverTraceBody } from "./anatomyDiscoveryTrace.js";
import {
    analyticsSchemaTool,
    createAnalyticsQueryTool,
    createExpandPreviewTool,
} from "./analyticsTool.js";
import { summarizeScoutStep } from "./scoutSteps.js";
import { DIMENSIONS } from "./dimensionDefs.js";
import {
    evidenceScopeSteps,
    isTraceScopeExpressibleDim,
    probeScopeMembership,
    withScopeGuard,
} from "./scoutScope.js";

export const THEME_WEIGHTS = ["dominant", "major", "minor"];
export const CONCENTRATIONS = ["concentrated", "mixed", "broad"];
export const MAX_THEMES = 8;
export const MAX_POCKETS = 6;
export const MAX_SPREAD_ENTRIES = 6;

const NOTE_MAX = 420;
const THINKING_NOTE_MAX = 280;
const LIVE_NOTE_MAX = 200;

const THEME_MIX_SCHEMA = {
    type: "array",
    description:
        "This endpoint's events per report theme, copied from your endpoint × cluster rows: "
        + "[{ name, events }] where `name` matches a themes[] entry EXACTLY. Include every theme "
        + "the endpoint is active in — the page draws the endpoint's shape of work from this. "
        + "Omit the field entirely if you did not measure the per-theme breakdown.",
    items: {
        type: "object",
        properties: {
            name: { type: "string", description: "A theme name, exactly as in themes[]." },
            events: { type: "integer", description: "The endpoint's events in that theme, copied exactly." },
        },
    },
};

const TARGET_SCHEMA = {
    type: "object",
    description:
        "Optional click-through: the scope step this claim is about. `dim` is a dashboard "
        + "dimension key (clusters, endpoints, sessions, agents, tools, models, …) and `id` "
        + "MUST be copied exactly from a row you queried. Omit the target rather than guessing.",
    properties: {
        dim: { type: "string" },
        id: { type: "string" },
        label: { type: "string", description: "Short display label for the step." },
    },
};

export const ANATOMY_REPORT_SCHEMA = {
    type: "object",
    properties: {
        profile: {
            type: "string",
            description: "2-4 sentences: what actually goes on in this scope — the dominant work, the notable pockets, how it is distributed. Plain prose, grounded in what you queried.",
        },
        runLabel: {
            type: "string",
            description: "A short cluster-style name for THIS investigation itself (3-6 words, e.g. 'scope_profile_theme_spread_survey') — it labels your run where a captured trace would carry its cluster name.",
        },
        themes: {
            type: "array",
            description: `3-${MAX_THEMES} work themes, most to least central. What the work is ABOUT, read from cluster names/descriptions and confirmed with texture — not tool mechanics.`,
            items: {
                type: "object",
                properties: {
                    name: { type: "string", description: "Short theme name, e.g. 'Endpoint agent capture pipeline'." },
                    weight: { type: "string", description: "dominant | major | minor — the share of this scope's activity the theme accounts for." },
                    description: { type: "string", description: "1-2 sentences on what this work is, concretely." },
                    events: { type: "integer", description: "The theme's rolled-up event count for the window, copied EXACTLY from the cluster rollup you queried. Omit if not measured — never estimate." },
                    endpointCount: { type: "integer", description: "Distinct endpoints active in this theme, copied from your queries. Omit if not measured." },
                    basis: { type: "string", description: "The evidence: what you queried and what it showed (counts, spread). One line." },
                    target: TARGET_SCHEMA,
                },
            },
        },
        spread: {
            type: "object",
            description: "How the scope's activity distributes across endpoints.",
            properties: {
                summary: { type: "string", description: "2-3 sentences: how concentrated the work is, and what shape the distribution has." },
                concentration: { type: "string", description: "concentrated | mixed | broad" },
                generalists: {
                    type: "array",
                    description: `Up to ${MAX_SPREAD_ENTRIES} high-volume endpoints active across many themes.`,
                    items: {
                        type: "object",
                        properties: {
                            label: { type: "string", description: "Endpoint display name." },
                            note: { type: "string", description: "One line: what makes it a generalist (volume, theme count)." },
                            events: { type: "integer", description: "This endpoint's TOTAL event count in this scope and window — the SAME measure for every spread entry in both lanes, copied exactly from your queries. Omit if not measured; never substitute a different count." },
                            themeMix: THEME_MIX_SCHEMA,
                            target: TARGET_SCHEMA,
                        },
                    },
                },
                specialists: {
                    type: "array",
                    description: `Up to ${MAX_SPREAD_ENTRIES} endpoints representing distinct specialist archetypes — the long tail, not just more volume.`,
                    items: {
                        type: "object",
                        properties: {
                            label: { type: "string" },
                            note: { type: "string", description: "One line: the specialty this endpoint represents." },
                            events: { type: "integer", description: "This endpoint's TOTAL event count in this scope and window — the SAME measure for every spread entry in both lanes, copied exactly from your queries. Omit if not measured; never substitute a different count." },
                            themeMix: THEME_MIX_SCHEMA,
                            target: TARGET_SCHEMA,
                        },
                    },
                },
                basis: { type: "string", description: "The evidence the spread read rests on. One line." },
            },
        },
        pockets: {
            type: "array",
            description: `0-${MAX_POCKETS} specialist pockets the aggregate hides — distinct bodies of work too small to surface as a top theme but coherent enough to matter.`,
            items: {
                type: "object",
                properties: {
                    name: { type: "string" },
                    description: { type: "string", description: "1-2 sentences on what this pocket is." },
                    whyNotable: { type: "string", description: "One line: why a reader of this scope should know it exists." },
                    basis: { type: "string", description: "The evidence. One line." },
                    target: TARGET_SCHEMA,
                },
            },
        },
    },
    required: ["profile", "themes", "spread"],
};

const SYSTEM_PROMPT = [
    "You profile ONE scope of an organization's captured AI-agent traces, so a reader opening",
    "that scope understands what is actually going on there. You return a structured report:",
    "a short PROFILE, the work THEMES, the endpoint SPREAD, and the specialist POCKETS the",
    "aggregate hides.",
    "",
    "Stay inside the scope you are given. Every query you run must carry the scope's filters",
    "and the given time range. Sample, don't scan: aggregate first (clusters, counts by",
    "endpoint), then read a handful of prompt summaries to confirm what a candidate theme is",
    "about. Avoid raw event-body scans — summaries and aggregates are enough to report from.",
    "",
    "Ground every claim. Each theme, each pocket, and the spread carry a `basis` naming what",
    "you queried and what it showed. A claim whose basis you cannot state is not a claim —",
    "drop it. Targets are click-throughs for the reader: copy `id` values EXACTLY from rows",
    "you queried, and omit the target rather than guessing one.",
    "",
    "Describe work at the level of themes and endpoints. An endpoint is a machine: say what",
    "work runs on it, and never infer or characterize the person behind it.",
    "",
    "Narrate as you go: before each query, write ONE short line saying what you're checking",
    "and why — these lines become the run's audit trail beside the queries. When done call",
    "return_result with the report. Do not deliver the report as prose.",
].join("\n");

// The scope pinned first (the sub-agent has no dashboard context), then the
// survey recipe — the same themes → spread → texture walk the tenant profiler
// runs, minus the starter-card tail. The ILO caveats (cluster_subtree, no
// time-window on ILO_CLUSTER, auto-pinned generations) encode known pitfalls.
export function buildDiscoveryTask({ scopeText, rangeText, scopeEnforced = false }) {
    return [
        `Scope: ${scopeText || "the entire organization"}.`,
        `Time range: ${rangeText || "the dashboard's selected range"}. Apply it to every query.`,
        ...(scopeEnforced ? [
            "Every target you cite is validated against this scope after you return; a target",
            "with no activity inside the scope loses its click-through. Copy targets only from",
            "scope-filtered query results.",
        ] : []),
        "",
        "Profile this scope — a handful of queries:",
        "1. Themes — the top work-themes by recent activity: the named level-0 ILO clusters,",
        "   using the `cluster_subtree` filter to roll leaf activity up to each theme. (Don't",
        "   time-window ILO_CLUSTER itself; don't pin generations — they auto-pin.) Read the",
        "   cluster names AND descriptions — they say what the work is about.",
        "2. Spread — group endpoints by dominant theme via ILO_EVENT_CLUSTER (endpoint ×",
        "   cluster). Activity is extreme power-law: don't just take the busiest endpoints —",
        "   identify the few high-volume generalists AND a sample of distinct specialist",
        "   archetypes. Report how concentrated each theme is: a theme dominated by one",
        "   endpoint is a different fact than one spread across thirty.",
        "3. Texture — for the sampled themes and endpoints, glance beyond clusters: a few",
        "   enriched prompt summaries, the top resource paths and repositories, and the",
        "   agent/model mix, to ground what the work really is.",
        "4. Pockets — specialist work the aggregate hides. A distinct pocket often reveals an",
        "   objective of its own; say why the reader should know it exists.",
        "",
        "Then return the report: profile (2-4 sentences), themes most to least central, the",
        "spread, and any pockets — each claim with its basis. Include the measured counts,",
        "copied exactly from your query results: per-theme event totals and endpoint counts,",
        "and for every spread endpoint its TOTAL events (one consistent measure across both",
        "lanes) plus its per-theme `themeMix` from the endpoint × cluster rows. The page DRAWS",
        "the theme bar and each endpoint's shape-of-work radar from these numbers, so a count",
        "you didn't measure must be omitted, never estimated or swapped for a different metric.",
    ].join("\n");
}

const VALID_TARGET_DIMS = new Set(Object.keys(DIMENSIONS));

function normalizeTarget(t) {
    const dim = String(t?.dim || "").trim();
    const id = String(t?.id || "").trim();
    if (!dim || !id || !VALID_TARGET_DIMS.has(dim)) return null;
    return { dim, id, label: String(t?.label || "").trim() || null };
}

function oneOf(list, v, fallback) {
    const s = String(v || "").trim().toLowerCase();
    return list.includes(s) ? s : fallback;
}

function toCount(v) {
    const n = Number(v);
    return Number.isFinite(n) && n >= 0 ? Math.round(n) : null;
}

// Normalize the scout's raw return into the stored report shape. Nameless
// items drop, counts clamp, and targets survive only with a real dimension
// key and a non-empty id — an invalid target degrades to plain text, never to
// a dead click.
export function normalizeAnatomyReport(raw) {
    const themes = (Array.isArray(raw?.themes) ? raw.themes : [])
        .map((t) => ({
            name: String(t?.name || "").trim(),
            weight: oneOf(THEME_WEIGHTS, t?.weight, "minor"),
            description: String(t?.description || "").trim(),
            events: toCount(t?.events),
            endpointCount: toCount(t?.endpointCount),
            basis: String(t?.basis || "").trim() || null,
            target: normalizeTarget(t?.target),
        }))
        .filter((t) => t.name)
        .slice(0, MAX_THEMES);

    const spreadRaw = raw?.spread && typeof raw.spread === "object" ? raw.spread : {};
    const entryOf = (e) => ({
        label: String(e?.label || "").trim(),
        note: String(e?.note || "").trim() || null,
        events: toCount(e?.events),
        themeMix: (Array.isArray(e?.themeMix) ? e.themeMix : [])
            .map((m) => ({ name: String(m?.name || "").trim(), events: toCount(m?.events) }))
            .filter((m) => m.name && m.events != null)
            .slice(0, MAX_THEMES + 4),
        target: normalizeTarget(e?.target),
    });
    const spread = {
        summary: String(spreadRaw.summary || "").trim(),
        concentration: oneOf(CONCENTRATIONS, spreadRaw.concentration, "mixed"),
        generalists: (Array.isArray(spreadRaw.generalists) ? spreadRaw.generalists : [])
            .map(entryOf).filter((e) => e.label).slice(0, MAX_SPREAD_ENTRIES),
        specialists: (Array.isArray(spreadRaw.specialists) ? spreadRaw.specialists : [])
            .map(entryOf).filter((e) => e.label).slice(0, MAX_SPREAD_ENTRIES),
        basis: String(spreadRaw.basis || "").trim() || null,
    };

    const pockets = (Array.isArray(raw?.pockets) ? raw.pockets : [])
        .map((p) => ({
            name: String(p?.name || "").trim(),
            description: String(p?.description || "").trim(),
            whyNotable: String(p?.whyNotable || "").trim() || null,
            basis: String(p?.basis || "").trim() || null,
            target: normalizeTarget(p?.target),
        }))
        .filter((p) => p.name)
        .slice(0, MAX_POCKETS);

    return {
        profile: String(raw?.profile || "").trim(),
        runLabel: String(raw?.runLabel || "").trim() || null,
        themes,
        spread,
        pockets,
    };
}

const TARGET_PROBE_CONCURRENCY = 4;

// Mechanical scope enforcement over a normalized report's targets. Each
// unique target is probed for at least one prompt INSIDE the run's scope; a
// target with none is stripped in place, so its claim renders as plain text
// instead of a click-through that lands on an empty slice of the scope.
// Probes are only possible where the traces query can express both the scope
// and the target dim; anything else is left alone and disclosed:
//   { droppedTargets, unenforcedDims, unprobeableDims, checked }.
// A probe that ERRORS keeps its target (an infra failure is not evidence the
// claim is out of scope).
export async function enforceReportTargets(report, { scopePath = [], range = null, signal = null } = {}) {
    const none = { droppedTargets: [], unenforcedDims: [], unprobeableDims: [], checked: 0 };
    const { steps, dropped: unenforcedDims } = evidenceScopeSteps(scopePath);
    if (!steps.length) return { ...none, unenforcedDims };

    const holders = [
        ...report.themes,
        ...(report.spread?.generalists || []),
        ...(report.spread?.specialists || []),
        ...report.pockets,
    ].filter((h) => h?.target);

    const unprobeableDims = [];
    const unique = new Map();
    for (const { target } of holders) {
        if (!isTraceScopeExpressibleDim(target.dim)) {
            if (!unprobeableDims.includes(target.dim)) unprobeableDims.push(target.dim);
            continue;
        }
        const key = `${target.dim}::${target.id}`;
        if (!unique.has(key)) unique.set(key, target);
    }
    const entries = [...unique.entries()];
    if (!entries.length) return { droppedTargets: [], unenforcedDims, unprobeableDims, checked: 0 };

    const settled = await runWithConcurrency(entries.map(([, t]) => async () => {
        if (signal?.aborted) throw new Error("aborted");
        return probeScopeMembership({ scopeSteps: steps, dim: t.dim, id: t.id, range });
    }), TARGET_PROBE_CONCURRENCY);

    const inScope = new Map();
    settled.forEach((r, i) => inScope.set(entries[i][0], r.ok ? r.value : true));

    const droppedTargets = [];
    for (const holder of holders) {
        const t = holder.target;
        if (!t || inScope.get(`${t.dim}::${t.id}`) !== false) continue;
        if (!droppedTargets.some((d) => d.dim === t.dim && d.id === t.id)) {
            droppedTargets.push({ dim: t.dim, id: t.id, label: t.label || null });
        }
        holder.target = null;
    }
    return { droppedTargets, unenforcedDims, unprobeableDims, checked: entries.length };
}

// Reader-facing lines for a run's mechanical scope enforcement
// (runAnatomyDiscovery's `scope` result). Persisted on the report's meta so
// the Discover panel can say what was removed or left unchecked.
export function scopeNotesFor(scope) {
    if (!scope) return [];
    const notes = [];
    const plural = (n, one, many) => (n === 1 ? one : many);
    if (scope.droppedTargets?.length) {
        const names = scope.droppedTargets.map((t) => t.label || `${t.dim} ${String(t.id).slice(0, 12)}`).join(", ");
        notes.push(`${scope.droppedTargets.length} cited ${plural(scope.droppedTargets.length, "target has", "targets have")} no activity in this scope; ${plural(scope.droppedTargets.length, "its click-through was", "their click-throughs were")} removed: ${names}`);
    }
    if (scope.queryWarnings) {
        notes.push(`${scope.queryWarnings} scout ${plural(scope.queryWarnings, "query", "queries")} did not reference the scope filter; flagged in the run trail`);
    }
    const unchecked = [...(scope.unenforcedDims || []), ...(scope.unprobeableDims || [])];
    if (unchecked.length) {
        notes.push(`scope checks could not cover: ${[...new Set(unchecked)].join(", ")}`);
    }
    return notes;
}

// Column choice for the spread's radar grid. Auto-fill packed cards left
// against a row of empty tracks on wide panels; this picks a column count
// deliberately: as dense as the width allows, never a lone-orphan last row
// (n % cols === 1 reads as a mistake), trailing empty tracks only mildly
// discouraged so a 3-card lane under a 4-card lane keeps the shared rhythm.
// One count for ALL lanes — mismatched lane columns is the raggedness this
// exists to remove. Pure and unit-tested; the caller supplies the measured
// width and falls back sensibly while unmeasured.
export function radarGridCols({ width, counts, minCell = 150, gap = 14 }) {
    const maxN = Math.max(0, ...counts.map((n) => (Number.isFinite(n) ? n : 0)));
    if (!maxN) return 1;
    if (!Number.isFinite(width) || width <= 0) return Math.min(4, maxN);
    const fit = Math.max(1, Math.floor((width + gap) / (minCell + gap)));
    const hi = Math.min(fit, maxN);
    let best = hi;
    let bestScore = -Infinity;
    for (let c = hi; c >= 1; c--) {
        let score = c * 10;
        for (const n of counts) {
            if (!n) continue;
            if (n > c && n % c === 1) score -= 25;
            if (n < c) score -= (c - n) * 2;
        }
        if (score > bestScore) {
            bestScore = score;
            best = c;
        }
    }
    return best;
}

export const WEIGHT_POINTS = { dominant: 3, major: 2, minor: 1 };

// Segment shares for the theme bar. Measured events win; when any theme lacks
// a count (a report from before counts were captured, or a scout that skipped
// one) the WHOLE bar falls back to weight points — segments stay honest
// relative to each other rather than mixing measured and invented widths.
export function themeShares(themes) {
    const list = Array.isArray(themes) ? themes : [];
    if (!list.length) return { mode: "none", segments: [] };
    const measured = list.every((t) => Number.isFinite(t?.events) && t.events > 0);
    const value = measured ? (t) => t.events : (t) => WEIGHT_POINTS[t?.weight] || 1;
    const total = list.reduce((s, t) => s + value(t), 0) || 1;
    return {
        mode: measured ? "events" : "weight",
        segments: list.map((t) => ({ ...t, share: value(t) / total })),
    };
}

function clipNote(text, max) {
    const t = String(text).trim();
    if (t.length <= max) return t;
    const slice = t.slice(0, max);
    const sentence = Math.max(slice.lastIndexOf(". "), slice.lastIndexOf(".\n"));
    if (sentence > max * 0.5) return slice.slice(0, sentence + 1);
    const word = slice.lastIndexOf(" ");
    return `${slice.slice(0, word > max * 0.5 ? word : max)}…`;
}

const PREVIEW_MAX_ROWS = 20;
const PREVIEW_MAX_COLS = 12;
const PREVIEW_CELL_MAX = 120;

function columnLabel(c) {
    if (typeof c === "string") return c;
    return String(c?.outputAlias || c?.alias || c?.name || c?.field?.field || c?.field || "").trim() || "col";
}

function cellValue(v) {
    if (v == null) return "";
    if (typeof v === "number") return v;
    const s = typeof v === "string" ? v : JSON.stringify(v);
    return s.length > PREVIEW_CELL_MAX ? `${s.slice(0, PREVIEW_CELL_MAX - 1)}…` : s;
}

// A renderable slice of one query's ACTUAL result — the same {columns, rows}
// payload the chat's analytics table renders, capped hard so a stored trail
// stays within localStorage's means. null when the payload isn't row-shaped.
export function resultPreviewOf(parsed) {
    if (!parsed || !Array.isArray(parsed.rows) || !parsed.rows.length) return null;
    const columns = (Array.isArray(parsed.columns) ? parsed.columns : [])
        .slice(0, PREVIEW_MAX_COLS)
        .map(columnLabel);
    const rows = parsed.rows.slice(0, PREVIEW_MAX_ROWS).map((r) => {
        const values = Array.isArray(r?.values) ? r.values : Array.isArray(r) ? r : columns.map((c) => r?.[c]);
        return values.slice(0, PREVIEW_MAX_COLS).map(cellValue);
    });
    const total = Number.isFinite(parsed?._preview?.total) ? parsed._preview.total : parsed.rows.length;
    return { columns, rows, total };
}

// The LLM-visible analytics payload is JSON text carrying rows (and a
// `_preview.total` when clipped) — parse it back out so the trail carries the
// run's REAL results: the count, and a capped renderable preview.
function resultOf(resultMsg) {
    const text = Array.isArray(resultMsg?.content)
        ? resultMsg.content.filter((c) => c?.type === "text").map((c) => c.text).join("")
        : null;
    if (!text) return { rows: null, preview: null };
    try {
        const parsed = JSON.parse(text);
        const preview = resultPreviewOf(parsed);
        const total = parsed?._preview?.total;
        const rows = Number.isFinite(total) ? total : Array.isArray(parsed?.rows) ? parsed.rows.length : null;
        return { rows, preview };
    } catch {
        return { rows: null, preview: null };
    }
}

// Grouped methods trail from the sub-agent's raw message history — the same
// shape buildScoutTrace derives from a chat transcript, but sourced from
// pi-agent-core messages (this run never lives in a chat). For the gap before
// a step the LAST assistant text wins; thinking stands in when the model
// wrote nothing. Returns [{ note: { text, thinking } | null, steps }].
export function buildDiscoveryTrail(messages) {
    const resultsByCallId = new Map();
    for (const m of messages || []) {
        if (m?.role === "toolResult" && m.toolCallId && !resultsByCallId.has(m.toolCallId)) {
            resultsByCallId.set(m.toolCallId, m);
        }
    }
    const groups = [];
    let pendingText = null;
    let pendingThinking = null;
    const takeNote = () => {
        const note = pendingText
            ? { text: clipNote(pendingText, NOTE_MAX), thinking: false }
            : pendingThinking
                ? { text: clipNote(pendingThinking, THINKING_NOTE_MAX), thinking: true }
                : null;
        pendingText = null;
        pendingThinking = null;
        return note;
    };
    for (const m of messages || []) {
        if (m?.role !== "assistant") continue;
        for (const c of Array.isArray(m.content) ? m.content : []) {
            if (c?.type === "text" && c.text?.trim()) {
                pendingText = c.text;
            } else if (c?.type === "thinking" && c.text?.trim()) {
                pendingThinking = c.text;
            } else if (c?.type === "toolCall" && c.name) {
                const note = takeNote();
                if (note || !groups.length) groups.push({ note, steps: [] });
                // The run's ending is part of the method: return_result maps
                // to a terminal `report` step — the moment the scout committed
                // the report this page renders — and the wrap-up narration
                // that precedes it attaches as that step's note instead of
                // being dropped.
                if (c.name === "return_result") {
                    groups[groups.length - 1].steps.push({
                        name: "report",
                        summary: "return_result — committed the report rendered above",
                        rows: null,
                        error: false,
                        preview: null,
                    });
                    continue;
                }
                const res = c.id ? resultsByCallId.get(c.id) : null;
                const step = summarizeScoutStep({
                    name: c.name,
                    args: c.arguments ?? c.input ?? c.args,
                    status: res?.isError ? "error" : "done",
                });
                const { rows, preview } = resultOf(res);
                step.rows = rows;
                step.preview = preview;
                groups[groups.length - 1].steps.push(step);
            }
        }
    }
    return groups;
}

// Live progress for the running panel: how many queries so far, and the
// scout's latest narration line. Derived from the child's message state on
// (throttled) events rather than from event payloads, so it needs no
// knowledge of pi-agent-core's event vocabulary.
export function liveProgress(messages) {
    let queries = 0;
    let note = null;
    for (const m of messages || []) {
        if (m?.role !== "assistant") continue;
        for (const c of Array.isArray(m.content) ? m.content : []) {
            if (c?.type === "toolCall" && c.name && c.name !== "return_result") queries += 1;
            else if (c?.type === "text" && c.text?.trim()) note = c.text;
        }
    }
    return { queries, note: note ? clipNote(note, LIVE_NOTE_MAX) : null };
}

// Compact lane label for a method step: the entity slice of an analytics
// summary ("ilo_cluster + endpoint"), or a short alias for the other tools.
export function stepLaneLabel(step) {
    if (step?.name === "analytics_query") {
        const entity = String(step.summary || "").split(" · ")[0].trim();
        return entity || "query";
    }
    if (step?.name === "analytics_describe_schema") return "schema";
    if (step?.name === "expand_preview") return "expand";
    return step?.name || "tool";
}

// The method trail as a trace: lanes = the entities the scout queried (its
// subjects), columns = the query sequence (its turns), narration beats on
// their own conversation-style lane. Same grammar as the trace-detail Lanes
// view, so the scout's investigation reads like any other trace.
// Returns { lanes: [{ label, marks: [{ col, error, rows, summary, name }],
// queries, rows }], cols, narration: [{ col, text, thinking }] } with lanes
// in first-appearance (investigation) order.
export function methodLanes(trail) {
    const lanes = [];
    const byLabel = new Map();
    const narration = [];
    let col = 0;
    for (const g of trail || []) {
        if (g?.note?.text) narration.push({ col, text: g.note.text, thinking: Boolean(g.note.thinking) });
        for (const s of g?.steps || []) {
            const label = stepLaneLabel(s);
            let lane = byLabel.get(label);
            if (!lane) {
                lane = { label, marks: [], queries: 0, rows: 0 };
                byLabel.set(label, lane);
                lanes.push(lane);
            }
            lane.marks.push({
                col,
                error: Boolean(s.error),
                rows: Number.isFinite(s.rows) ? s.rows : null,
                summary: s.summary || "",
                name: s.name,
                preview: s.preview || null,
            });
            lane.queries += 1;
            if (Number.isFinite(s.rows)) lane.rows += s.rows;
            col += 1;
        }
    }
    return { lanes, cols: col, narration };
}

const PROGRESS_THROTTLE_MS = 350;

// Run the discovery scout over one scope. Always resolves:
// { status: "done", report, trail } | { status: "aborted" } |
// { status: "error", error, lastText? }.
export async function runAnatomyDiscovery({
    scopeText,
    rangeText,
    scopePath = [],
    range = null,
    model = null,
    thinkingLevel = null,
    signal,
    onProgress,
} = {}) {
    const nsKey = "anatomy-discover";
    const getNamespaceKey = () => nsKey;
    let scopeWarnings = 0;
    const tools = [
        analyticsSchemaTool,
        withScopeGuard(createAnalyticsQueryTool({ getNamespaceKey }), {
            scopePath,
            onWarning: () => { scopeWarnings += 1; },
        }),
        createExpandPreviewTool({ getNamespaceKey }),
    ];

    let handle = null;
    let lastTick = 0;
    const handleEvent = () => {
        const now = Date.now();
        if (now - lastTick < PROGRESS_THROTTLE_MS) return;
        lastTick = now;
        const messages = handle?.child?.state?.messages;
        if (!messages) return;
        try { onProgress?.(liveProgress(messages)); } catch { /* progress is cosmetic */ }
    };

    let r;
    try {
        handle = await startSubAgent({
            role: "primary",
            model,
            thinkingLevel,
            signal,
            onEvent: onProgress ? handleEvent : undefined,
            tools,
            outputSchema: ANATOMY_REPORT_SCHEMA,
            systemPrompt: SYSTEM_PROMPT,
            task: buildDiscoveryTask({ scopeText, rangeText, scopeEnforced: scopePath.length > 0 }),
        });
        r = await handle.promise;
    } catch (e) {
        return { status: "error", error: e instanceof Error ? e : new Error(String(e)) };
    }

    if (r.status === "aborted") return { status: "aborted" };
    if (r.status !== "done" || !r.result || typeof r.result !== "object") {
        return {
            status: "error",
            error: r.error || new Error(r.reason || "the scout returned no report"),
            lastText: r.lastAssistantText || null,
        };
    }
    const report = normalizeAnatomyReport(r.result);
    if (!report.profile && !report.themes.length) {
        return { status: "error", error: new Error("discovery returned neither a profile nor themes") };
    }
    // Scope enforcement over the report's click-throughs. Probe failures keep
    // their targets; an aborted run still returns the report unprobed rather
    // than erroring after the model spend.
    let scopeCheck = { droppedTargets: [], unenforcedDims: [], unprobeableDims: [], checked: 0 };
    try {
        scopeCheck = await enforceReportTargets(report, { scopePath, range, signal });
    } catch { /* enforcement must not lose a finished report */ }
    // Capture the run AS a trace: the child's own message history, in the
    // request-body shape the trace sidebar parses. Model id read from the
    // child's resolved runtime (falling back to the caller's override); the
    // store may drop the body under quota pressure, so it is additive to the
    // report, never load-bearing.
    const resolvedModel = handle?.child?.state?.model?.id || handle?.child?.state?.model?.modelId || model || null;
    return {
        status: "done",
        report,
        trail: buildDiscoveryTrail(r.messages),
        traceBody: buildDiscoverTraceBody({ messages: r.messages, model: resolvedModel, system: SYSTEM_PROMPT, tools }),
        model: resolvedModel,
        scope: { ...scopeCheck, queryWarnings: scopeWarnings },
    };
}
